import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Globe, ExternalLink, Link2, AlertTriangle } from 'lucide-react';
import { useAdminTranslation } from '../../utils/useAdminTranslation';

// Country to flag - using flag CDN images
const getCountryFlag = (country) => {
  if (!country) return null;
  const countryUpper = country.toUpperCase().trim();
  const countryCodes = {
    'SPAIN': 'es', 'SPAIN (SPANISH)': 'es', 'ESPAÑA': 'es',
    'FRANCE': 'fr', 'FRANCE (FRENCH)': 'fr', 'FRANCIA': 'fr',
    'GERMANY': 'de', 'GERMANY (GERMAN)': 'de', 'ALEMANIA': 'de',
    'ITALY': 'it', 'ITALY (ITALIAN)': 'it', 'ITALIA': 'it',
    'PORTUGAL': 'pt', 'PORTUGAL (PORTUGUESE)': 'pt',
    'UK': 'gb', 'UNITED KINGDOM': 'gb', 'REINO UNIDO': 'gb', 'GB': 'gb', 'GREAT BRITAIN': 'gb',
    'USA': 'us', 'UNITED STATES': 'us', 'ESTADOS UNIDOS': 'us', 'US': 'us',
    'NETHERLANDS': 'nl', 'HOLANDA': 'nl', 'PAISES BAJOS': 'nl',
    'BELGIUM': 'be', 'BÉLGICA': 'be', 'BELGICA': 'be',
    'AUSTRIA': 'at',
    'SWITZERLAND': 'ch', 'SUIZA': 'ch',
    'POLAND': 'pl', 'POLONIA': 'pl',
    'CZECH REPUBLIC': 'cz', 'CZECHIA': 'cz', 'REPÚBLICA CHECA': 'cz',
    'SWEDEN': 'se', 'SUECIA': 'se',
    'NORWAY': 'no', 'NORUEGA': 'no',
    'DENMARK': 'dk', 'DINAMARCA': 'dk',
    'FINLAND': 'fi', 'FINLANDIA': 'fi',
    'IRELAND': 'ie', 'IRLANDA': 'ie',
    'GREECE': 'gr', 'GRECIA': 'gr',
    'ROMANIA': 'ro', 'RUMANIA': 'ro',
    'HUNGARY': 'hu', 'HUNGRÍA': 'hu', 'HUNGRIA': 'hu',
    'BULGARIA': 'bg',
    'CROATIA': 'hr', 'CROACIA': 'hr',
    'SLOVAKIA': 'sk', 'ESLOVAQUIA': 'sk',
    'SLOVENIA': 'si', 'ESLOVENIA': 'si',
    'LITHUANIA': 'lt', 'LITUANIA': 'lt',
    'LATVIA': 'lv', 'LETONIA': 'lv',
    'ESTONIA': 'ee',
    'CYPRUS': 'cy', 'CHIPRE': 'cy',
    'MALTA': 'mt',
    'LUXEMBOURG': 'lu', 'LUXEMBURGO': 'lu',
    'BRAZIL': 'br', 'BRASIL': 'br',
    'MEXICO': 'mx', 'MÉXICO': 'mx',
    'ARGENTINA': 'ar',
    'CHILE': 'cl',
    'COLOMBIA': 'co',
    'PERU': 'pe', 'PERÚ': 'pe',
    'INDIA': 'in',
    'CHINA': 'cn',
    'JAPAN': 'jp', 'JAPÓN': 'jp', 'JAPON': 'jp',
    'SOUTH KOREA': 'kr', 'COREA DEL SUR': 'kr',
    'AUSTRALIA': 'au',
    'CANADA': 'ca', 'CANADÁ': 'ca',
    'RUSSIA': 'ru', 'RUSIA': 'ru',
    'TURKEY': 'tr', 'TURQUÍA': 'tr', 'TURQUIA': 'tr',
    'INDONESIA': 'id',
    'THAILAND': 'th', 'TAILANDIA': 'th',
    'VIETNAM': 'vn',
    'MALAYSIA': 'my', 'MALASIA': 'my',
    'SINGAPORE': 'sg', 'SINGAPUR': 'sg',
    'PHILIPPINES': 'ph', 'FILIPINAS': 'ph',
    'EGYPT': 'eg', 'EGIPTO': 'eg',
    'SOUTH AFRICA': 'za', 'SUDÁFRICA': 'za', 'SUDAFRICA': 'za',
    'ISRAEL': 'il',
    'UNITED ARAB EMIRATES': 'ae', 'UAE': 'ae', 'EMIRATOS ÁRABES': 'ae',
    'SAUDI ARABIA': 'sa', 'ARABIA SAUDITA': 'sa',
    'UKRAINE': 'ua', 'UCRANIA': 'ua',
    'GLOBAL': null,
    'INTERNATIONAL': null,
  };
  const code = countryCodes[countryUpper];
  return code ? `https://flagcdn.com/24x18/${code}.png` : null;
};

// Parse ALL link types from the field and return array of badges
const getLinkTypeBadges = (linkType) => {
  if (!linkType || linkType === 'No') return [];
  const badges = [];
  const lower = linkType.toLowerCase();
  const parts = lower.split(',').map(p => p.trim());

  for (const part of parts) {
    if (part.includes('dofollow')) {
      badges.push({ label: 'DoFollow', color: 'bg-blue-100 text-blue-700' });
    }
    if (part.includes('nofollow')) {
      badges.push({ label: 'NoFollow', color: 'bg-slate-100 text-slate-600' });
    }
    if (part.includes('sponsor')) {
      badges.push({ label: 'Sponsored', color: 'bg-amber-100 text-amber-700' });
    }
    if (part.includes('ugc')) {
      badges.push({ label: 'UGC', color: 'bg-purple-100 text-purple-700' });
    }
  }

  return badges;
};

// Get sensitive categories that the product accepts
const getSensitiveCategories = (product, tx) => {
  const categories = [];
  if (product.casino) {
    categories.push({ name: 'Casino', color: 'bg-red-100 text-red-700' });
  }
  if (product.cbd) {
    categories.push({ name: 'CBD', color: 'bg-green-100 text-green-700' });
  }
  if (product.gambling) {
    categories.push({ name: tx('Apuestas', 'Gambling'), color: 'bg-orange-100 text-orange-700' });
  }
  if (product.cryptocurrency) {
    categories.push({ name: 'Crypto', color: 'bg-yellow-100 text-yellow-700' });
  }
  if (product.sex) {
    categories.push({ name: tx('Sexo', 'Sex'), color: 'bg-pink-100 text-pink-700' });
  }
  if (product.erotic) {
    categories.push({ name: tx('Erótico', 'Erotic'), color: 'bg-pink-100 text-pink-700' });
  }
  if (product.vpn) {
    categories.push({ name: 'VPN', color: 'bg-blue-100 text-blue-700' });
  }
  if (product.tarot) {
    categories.push({ name: 'Tarot', color: 'bg-purple-100 text-purple-700' });
  }
  if (product.porn) {
    categories.push({ name: tx('Porno', 'Porn'), color: 'bg-red-100 text-red-700' });
  }
  if (product.cannabis) {
    categories.push({ name: 'Cannabis', color: 'bg-green-100 text-green-700' });
  }
  return categories;
};

export const ProductDetailDialog = ({ 
  product, 
  open, 
  onOpenChange, 
  onFindSimilar, 
  language = 'es',
  texts 
}) => {
  const { tx } = useAdminTranslation();
  
  if (!product) return null;

  const t = texts || {
    moreInfoTitle: 'Información detallada del producto',
    tableCountry: 'País',
    tableLanguage: 'Idioma',
    tableSiteType: 'Tipo de Medio',
    tablePrice: 'Precio',
    tablePriceSensitive: 'Precio Sensible',
    tableDr: 'DR',
    tableTraffic: 'Tráfico',
    tableLinkType: 'Tipo Enlace',
    linkDuration: 'Duración Enlace',
    tableCategory: 'Categoría',
    tableTag: 'Etiqueta',
    tableSimilarSites: 'Sitios Similares',
    noSensitivePrice: 'No disponible',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="product-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Globe className="h-5 w-5 text-indigo-600" />
            {product.website?.replace(/^https?:\/\//, '').replace(/\/$/, '')}
          </DialogTitle>
          <DialogDescription>
            {t.moreInfoTitle}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Main Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">{t.tableCountry}</p>
              <p className="font-semibold flex items-center gap-2">
                {getCountryFlag(product.country) && (
                  <img 
                    src={getCountryFlag(product.country)} 
                    alt={product.country}
                    className="h-5 w-7 object-cover rounded-sm shadow-sm"
                  />
                )}
                {product.country || '-'}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">{t.tableLanguage}</p>
              <p className="font-semibold">{product.language || '-'}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">{t.tableSiteType}</p>
              <p className="font-semibold">{product.site_type || '-'}</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">{t.tablePrice}</p>
              <p className="font-bold text-emerald-600 text-lg">€{product.pvp || 0}</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">{t.tablePriceSensitive}</p>
              <p className="font-bold text-amber-600 text-lg">
                {(product.premium_price || product.premium_sale_price || product.price_sensitive) > 0 
                  ? `€${product.premium_price || product.premium_sale_price || product.price_sensitive}` 
                  : t.noSensitivePrice}
              </p>
            </div>
            <div className="bg-violet-50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">{t.tableDr}</p>
              <p className="font-bold text-violet-600 text-lg">{product.dr || '-'}</p>
            </div>
            <div className="bg-sky-50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">{t.tableTraffic}</p>
              <p className="font-bold text-sky-600 text-lg">{(product.traffic || product.organic_traffic || 0).toLocaleString()}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">{t.tableLinkType}</p>
              <div className="flex flex-wrap gap-1">
                {getLinkTypeBadges(product.link_type).map((badge, idx) => (
                  <Badge key={idx} className={`${badge.color} font-semibold`}>
                    {badge.label}
                  </Badge>
                ))}
                {getLinkTypeBadges(product.link_type).length === 0 && <p className="font-semibold">-</p>}
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">{t.linkDuration}</p>
              <p className="font-semibold">{product.link_duration || '-'}</p>
            </div>
          </div>
          
          {/* Category */}
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">{t.tableCategory}</p>
            <p className="font-medium text-sm">{product.category || '-'}</p>
          </div>
          
          {/* Sensitive Categories - ONLY show accepted ones */}
          {getSensitiveCategories(product, tx).length > 0 && (
            <div className="bg-gradient-to-r from-amber-50 to-rose-50 rounded-lg p-4 border border-amber-200">
              <p className="text-xs text-amber-700 font-semibold mb-3 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {tx('Temáticas Sensibles Aceptadas', 'Sensitive Topics Accepted')}
              </p>
              <div className="flex flex-wrap gap-2">
                {getSensitiveCategories(product, tx).map((cat, idx) => (
                  <Badge key={idx} className={`${cat.color} font-medium`}>
                    ✓ {cat.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Tag */}
          {product.ad_label && product.ad_label !== 'No' && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{t.tableTag}:</span>
              <Badge className="bg-rose-100 text-rose-700">{product.ad_label}</Badge>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => onFindSimilar(product)}
              className="flex items-center gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              data-testid="find-similar-button"
            >
              <Link2 className="h-4 w-4" />
              {t.tableSimilarSites}
            </Button>
            <Button
              asChild
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            >
              <a 
                href={product.website?.startsWith('http') ? product.website : `https://${product.website}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visit-site-button"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                {tx('Visitar Sitio', 'Visit Site')}
              </a>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductDetailDialog;
